<script>
    import { restore, reset } from "../../stores/common/persisted.js";
    import settings from "../../stores/settings.js";
    import windows from "../../stores/windows.js";
    import profile from "../../stores/profile.js";
    import Button from "../windows/Button.svelte";
    import { sleep } from "../../helpers/time.js";
    import t from "../../localization/t.js";

    let fileInput;

    function didClickSaveButton() {
        const event = new Event("beforeunload");
        window.dispatchEvent(event);

        const a = document.createElement("a");

        const blob = new Blob([JSON.stringify(localStorage)], {
            type: "application/octet-stream"
        });

        a.href = window.URL.createObjectURL(blob);
        a.download = "america.sav";
        a.click();
    }

    function didClickLoadButton() {
        fileInput.click();
        fileInput.addEventListener("change", didSelectFile);
    }

    function didSelectFile(e) {
        const [file] = e.target.files;

        if (file) {
            const reader = new FileReader();

            reader.onload = (e) => {
                const json = JSON.parse(e.target.result);
                restore(json);
            };

            reader.readAsText(file);
        }

        fileInput.removeEventListener("change", didSelectFile);
    }

    function didClickResetButton() {
        settings.reset();
    }

    async function performReset() {
        await Promise.all(
            $windows
                .filter(({ isClosed }) => !isClosed)
                .map(async (window, index) => {
                    await sleep(150 * index);
                    windows.close(window.id);
                })
        );

        await sleep(150);
        await reset();
    }

    function didClickRepaintButton() {
        windows.focus(
            windows.open("color", {
                onSelect(e) {
                    profile.repaint(e.detail.color);
                }
            })
        );
    }

    $: $settings.isResetRequested && performReset();
</script>

<div class="settings">
    <p>{t("settings.save_progress_description")}</p>
    <div class="upload">
        <input type="file" bind:this={fileInput} accept=".sav" />
    </div>
    <Button action={didClickSaveButton}>
        {t("settings.save_progress_action")}
    </Button>
    <Button action={didClickLoadButton}>
        {t("settings.load_progress_action")}
    </Button>
    <hr />

    <div class="color">
        <div class="flexible">
            <h1>Vehicle color</h1>
            <div class="spacer"></div>
            <Button action={didClickRepaintButton}>Repaint</Button>
        </div>
        <div class="swatch" style={`background-color: ${$profile.color}`}></div>
    </div>
    <hr />

    <h1>{t("settings.display_units")}</h1>
    <div class="units">
        <div class="field">
            <input type="radio" name="unit" id="imperial" checked />
            <label for="imperial">{t("settings.imperial")}</label>
        </div>
        <div class="field">
            <input type="radio" name="unit" id="metric" disabled />
            <label for="metric" class="disabled">
                {t("settings.metric")}
            </label>
        </div>
    </div>
    <hr />

    <p>{t("settings.start_over_description")}</p>
    <Button action={didClickResetButton}>
        {t("settings.start_over_action")}
    </Button>
</div>

<style>
    .settings {
        padding: var(--spacing);
    }

    p {
        padding-bottom: var(--spacing);
    }

    .upload {
        display: none;
    }

    hr {
        margin: calc(var(--spacing) * 2) 0;
        border-top: var(--border-color-dark) var(--chrome-depth) inset;
        border-right: var(--border-color-light) var(--chrome-depth) inset;
        border-bottom: var(--border-color-light) var(--chrome-depth) inset;
        border-left: var(--border-color-dark) var(--chrome-depth) inset;
    }

    .units {
        display: flex;
        margin-top: var(--chrome-depth);
    }

    .field {
        display: flex;
        align-items: center;
        gap: var(--chrome-depth);
    }

    .color {
        display: flex;
    }

    .flexible {
        flex: 1;
    }

    .swatch {
        aspect-ratio: 1;
        min-height: 100%;
        width: 37px;
        border-top: var(--border-color-dark) var(--chrome-depth) inset;
        border-right: var(--border-color-light) var(--chrome-depth) inset;
        border-bottom: var(--border-color-light) var(--chrome-depth) inset;
        border-left: var(--border-color-dark) var(--chrome-depth) inset;
    }

    .spacer {
        height: var(--spacing);
    }

    label {
        flex-grow: 1;
        margin-right: 10px;
    }

    label.disabled {
        opacity: 0.25;
    }

    input {
        appearance: none;
        border-top: var(--border-color-dark) var(--chrome-depth) inset;
        border-right: var(--border-color-light) var(--chrome-depth) inset;
        border-bottom: var(--border-color-light) var(--chrome-depth) inset;
        border-left: var(--border-color-dark) var(--chrome-depth) inset;
        border-radius: 8px;
        background: white;
        width: 16px;
        height: 16px;
    }

    input:checked {
        color: black;
    }

    input:before {
        position: relative;
        top: -7.5px;
        left: 0.5px;
        opacity: 0;
        content: "●";
        font-size: 19px;
    }

    @media only screen and (max-width: 734px) {
        input:before {
            top: -8.5px;
        }
    }

    input:checked:before {
        opacity: 1;
    }

    input:disabled {
        opacity: 0.5;
    }
</style>
